import React, {useCallback, useEffect, useRef, useState} from 'react';
import {EButton, EDialog, EIconButton} from 'webapp-lib/Components';
import {classy} from 'webapp-lib/utils';

import {Close} from '_/assets/images';

import {
  Actions,
  CloseButtonWrapper,
  Description,
  DialogHeading,
  DialogWrapper,
} from './styles';

export type DialogSizes =
  | 'tiny'
  | 'little'
  | 'small'
  | 'medium'
  | 'large'
  | 'huge';

export type Props = {
  actions?: React.ReactElement<typeof EButton>[];
  children?: React.ReactNode;
  isShown?: boolean;
  onClose?: () => void;
  headerText?: string;
  descriptionText?: string;
  className?: string;
  testId?: string;
  stepperActions?: boolean;
  size?: DialogSizes;
};

export const Dialog = (props: Props): React.ReactElement => (
  <BaseDialog {...props} />
);

export const NonClosableDialog = (
  props: Omit<Props, 'onClose'>
): React.ReactElement => <BaseDialog {...props} />;

const BaseDialog = ({
  actions,
  className,
  children,
  isShown = false,
  onClose,
  headerText,
  descriptionText,
  stepperActions = false,
  size,
  testId = 'dialog-background',
}: Partial<Props>): React.ReactElement => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [closeAllowed, setCloseAllowed] = useState(true);
  const hasActions = (actions?.length || 0) > 0;

  const possiblyDisabledOnClose = useCallback(() => {
    if (closeAllowed) {
      return onClose?.();
    }
  }, [closeAllowed, onClose]);

  useEffect(() => {
    const handleOnClose = (e: Event) => {
      const isCloseAllowed = (e as CustomEvent).detail !== 'started';
      setCloseAllowed(isCloseAllowed);
    };

    window.addEventListener('submitting', handleOnClose);
    return () => window.removeEventListener('submitting', handleOnClose);
  }, [onClose]);

  return (
    <EDialog
      className={className}
      data-testid={testId}
      open={isShown}
      tabIndex={0}
      onClose={possiblyDisabledOnClose}
    >
      {isShown ? (
        <DialogWrapper
          ref={contentRef}
          className={classy({
            InternalDialogWrapper: true,
            [size ?? '']: Boolean(size),
          })}
          hasActions={hasActions}
        >
          <DialogHeading>
            {headerText}
            {onClose ? (
              <CloseButtonWrapper>
                <EIconButton onClick={possiblyDisabledOnClose}>
                  <Close />
                </EIconButton>
              </CloseButtonWrapper>
            ) : null}
          </DialogHeading>
          {children ? children : <Description>{descriptionText}</Description>}
        </DialogWrapper>
      ) : null}
      {hasActions ? (
        <Actions stepperActions={stepperActions}>{actions}</Actions>
      ) : null}
    </EDialog>
  );
};
